import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Box, Table, TableContainer, TableRow, TableBody } from '@mui/material';
import { mdiDownload } from '@mdi/js';
import { Icon } from '@mdi/react';

import { TableHead, TableCell, TableSortButton } from 'common/components/Table';
import {
    IPortfolioReportOrderField,
    IContentTypeValue,
    IFeature,
    ExportPortfolioReportDocument,
    CurrentUserDocument,
    IOfferEventType,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { Typography } from 'common/components/Typography';
import { BoxLoader } from 'common/components/Loader';
import { FilterBar } from 'common/components/FilterBar';
import { Pagination } from 'common/components/Pagination';
import { useReportList } from 'report/hooks/useReportList';
import { useFeature } from 'common/hooks/useFeature';
import { Button } from 'common/components/Button';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { OfferReportTableRow } from 'report/components/OfferReportTable/OfferReportTableRow';

interface IProps {
    offerEventType?: IOfferEventType[];
}

export const OfferReportTable = ({ offerEventType }: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [showExportDialog, setShowExportDialog] = useState(false);
    const { canUse: canExport } = useFeature(IFeature.Export);

    // Get current user email to use in export dialog
    const { data: currentUserData, loading: loadingCurrentUser } =
        useQuery(CurrentUserDocument);
    const currentUserEmail = currentUserData?.currentUser?.email;

    const relatedObjectTypes = [IContentTypeValue.OfferEvent];

    const {
        items,
        loading: loadingReport,
        count,
        onSearch,
        onFilterChange,
        filters,
        filterValues,
        onFilterChangeValue,
        currentSort,
        onSort,
        setPage,
        paginationSettings,
        filterValuesForQuery,
    } = useReportList('offer', false, {
        relatedObjectTypes,
        offerEventType,
        withAggregatedFilterValues: true,
        isOffersReport: true,
    });

    const loading = loadingReport || loadingCurrentUser;

    const [exportReport, { loading: loadingExport }] = useMutation(
        ExportPortfolioReportDocument,
        {
            variables: {
                relatedObjectTypes,
                offerEventType,
                ...filterValuesForQuery,
            },
        }
    );

    const handleExport = async () => {
        try {
            const exportResults = await exportReport();
            const fileUrl = exportResults.data?.exportPortfolioReport?.fileUrl;

            if (fileUrl) {
                window.open(fileUrl, '_blank');
            } else {
                displaySnackbar(translate('reportPage.exportSuccess'), {
                    variant: 'success',
                });
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowExportDialog(false);
    };

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
    };

    const isSeminarType = offerEventType?.some(
        (type) => type === IOfferEventType.Meeting
    );

    return (
        <>
            <FilterBar
                useFilterDrawer
                barAppend={
                    canExport && (
                        <Button
                            disabled={count === 0}
                            startIcon={
                                <Icon path={mdiDownload} size="1.9rem" />
                            }
                            sx={{ flex: 1 }}
                            variant="outlined"
                            onClick={() => setShowExportDialog(true)}
                        />
                    )
                }
                filterDrawerProps={{
                    title: translate('reportPage.filterFormTraining.title'),
                    description: translate(
                        'reportPage.filterFormTraining.description'
                    ),
                    formProps: {
                        onReset: (values) => {
                            setPage(1);
                            onFilterChange(values);
                        },
                    },
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onChangeField={onFilterChangeValue}
                onFiltersChange={(values) => {
                    setPage(1);
                    onFilterChange(values);
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loading && !!items.length && (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('user')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.UserName
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.UserName
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('item')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.Title
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.Title
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('start')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.StartDate
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.StartDate
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('fulfilledOn')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.CompletionDate
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.CompletionDate
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('time')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.TimeSpent
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.TimeSpent
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('score')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.Score
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.Score
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items?.map((reportItem) => (
                                <OfferReportTableRow
                                    key={reportItem.id}
                                    reportItem={reportItem}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {loading && <BoxLoader />}

            {!loading && !items.length && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography>
                        {translate('noOptionsText.reportItems')}
                    </Typography>
                </Box>
            )}

            {!loading && (
                <Box sx={{ mt: 4 }}>
                    <Pagination
                        page={paginationSettings.page}
                        pageAmount={paginationSettings.pageAmount}
                        totalsAmount={paginationSettings.count}
                        totalsText={translate('portfolioReportItems', {
                            count,
                        })}
                        onChange={(page: number) => {
                            setPage(page);
                        }}
                    />
                </Box>
            )}

            <ConfirmDialog
                confirmText={translate('sendExport')}
                loading={loadingExport}
                open={showExportDialog}
                title={translate(
                    `reportPage.${isSeminarType ? 'exportSeminarDialog' : 'exportOfferDialog'}.title`
                )}
                onCancel={() => setShowExportDialog(false)}
                onClose={() => setShowExportDialog(false)}
                onConfirm={handleExport}
            >
                {translate(
                    `reportPage.${isSeminarType ? 'exportSeminarDialog' : 'exportOfferDialog'}.text`,
                    {
                        email: currentUserEmail,
                    }
                )}
            </ConfirmDialog>
        </>
    );
};
